import { useEffect, useState } from 'react'

import httpClient from '@/lib/api'
import type { DatasetKind } from '@/types/components/dataset-kind-chip'

const DATASET_KINDS: Array<DatasetKind> = [
  'consumer_panel', 'tracker', 'market_sizing', 'sales_transactions', 'other',
]

/**
 * Why the model judged a question not ready to chart (B1 `DatasetReadinessReason`,
 * D23's fixed list; B2 `KnowledgeDocumentDatasetReason`). Anything else the API
 * sends is dropped, never shown raw.
 */
export type DatasetReadinessReason =
  | 'labels_missing'
  | 'question_wording_missing'
  | 'ambiguous_columns'
  | 'structure_unclear'

const KNOWN_REASONS: Array<DatasetReadinessReason> = [
  'labels_missing',
  'question_wording_missing',
  'ambiguous_columns',
  'structure_unclear',
]

/**
 * B2 `KnowledgeDocumentDatasetState`: `listing` while the upload lists the
 * questions, `listed` once it has, `failed` when the file could not be read.
 */
export type DatasetReadinessState = 'listing' | 'listed' | 'failed'

const KNOWN_STATES: Array<DatasetReadinessState> = ['listing', 'listed', 'failed']

/** One question the model judged not ready to chart (by question id, never a row id). */
export interface DatasetNotReadyQuestion {
  questionId: string
  label: string
  reasons: Array<DatasetReadinessReason>
}

/**
 * The card's view of B2's `KnowledgeDocumentDatasetStatus`
 * (`GET /knowledge/documents/:token/dataset`, field names as B2 emits them).
 * Readiness is written per question on first use (owner design), so `ready`
 * and `notReady` count only the questions already checked; `questions` is every
 * question the upload listed.
 */
export interface KnowledgeDocumentDatasetReadiness {
  audience: { token: string; name: string } | null
  kind: DatasetKind
  isRespondentLevel: boolean
  state: DatasetReadinessState
  questions: number
  ready: number
  notReady: number
  notReadyQuestions: Array<DatasetNotReadyQuestion>
}

/** The document detail fields the hook keys on: the token, plus the stage fields that refetch it. */
export interface DatasetReadinessSource {
  token: string
  indexingStatus?: string
  extractionState?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

function isKnownReason(value: unknown): value is DatasetReadinessReason {
  return typeof value === 'string' && KNOWN_REASONS.includes(value as DatasetReadinessReason)
}

function isKnownState(value: unknown): value is DatasetReadinessState {
  return typeof value === 'string' && KNOWN_STATES.includes(value as DatasetReadinessState)
}

function parseReasons(value: unknown): Array<DatasetReadinessReason> {
  const reasons: Array<DatasetReadinessReason> = []
  if (!Array.isArray(value)) {
    return reasons
  }
  for (const entry of value) {
    if (isKnownReason(entry) && !reasons.includes(entry)) {
      reasons.push(entry)
    }
  }
  return reasons
}

function parseNotReadyQuestions(value: unknown): Array<DatasetNotReadyQuestion> | null {
  if (!Array.isArray(value)) {
    return null
  }
  const questions: Array<DatasetNotReadyQuestion> = []
  for (const entry of value) {
    if (!isRecord(entry) || typeof entry.questionId !== 'string' || !entry.questionId) {
      continue
    }
    const label =
      typeof entry.wording === 'string' && entry.wording.trim()
        ? entry.wording.trim()
        : entry.questionId
    questions.push({ questionId: entry.questionId, label, reasons: parseReasons(entry.reasons) })
  }
  return questions
}

/**
 * Normalizes B2's `dataset` status. Fails closed: `null` (the card renders
 * nothing) for a non dataset, an unknown `state`, or counts that are missing or
 * not whole numbers, so a shape mismatch never shows a count that looks real.
 */
export function parseDatasetReadiness(dataset: unknown): KnowledgeDocumentDatasetReadiness | null {
  if (!isRecord(dataset) || !isKnownState(dataset.state) || !isRecord(dataset.readiness)) {
    return null
  }
  if ((dataset.kind !== undefined && !DATASET_KINDS.includes(dataset.kind as DatasetKind)) ||
    (dataset.isRespondentLevel !== undefined && typeof dataset.isRespondentLevel !== 'boolean')) {
    return null
  }
  const { readiness } = dataset
  if (!isCount(readiness.questions) || !isCount(readiness.ready) || !isCount(readiness.notReady)) {
    return null
  }
  if (readiness.ready + readiness.notReady > readiness.questions) {
    return null
  }
  const notReadyQuestions = parseNotReadyQuestions(readiness.notReadyQuestions)
  if (!notReadyQuestions) {
    return null
  }
  return {
    kind: (dataset.kind ?? 'other') as DatasetKind,
    isRespondentLevel: dataset.isRespondentLevel === true,
    audience: isRecord(dataset.audience) && typeof dataset.audience.token === 'string' && typeof dataset.audience.name === 'string'
      ? { token: dataset.audience.token, name: dataset.audience.name } : null,
    state: dataset.state,
    questions: readiness.questions,
    ready: readiness.ready,
    notReady: readiness.notReady,
    notReadyQuestions,
  }
}

/** Reads B2's dataset status for one Library document; `null` for a non dataset or a bad shape. */
export async function fetchDatasetReadiness(
  documentToken: string,
): Promise<KnowledgeDocumentDatasetReadiness | null> {
  const response = await httpClient.get(
    `/knowledge/documents/${encodeURIComponent(documentToken)}/dataset`,
  )
  const body: unknown = response.data
  return isRecord(body) ? parseDatasetReadiness(body.dataset) : null
}

/**
 * Read-only readiness of a Library dataset. Refetches when the document's
 * stage fields move, so a dataset still listing its questions updates with the
 * page's own document reloads. Any request error renders nothing.
 */
export function useDatasetReadiness(
  document: DatasetReadinessSource,
): KnowledgeDocumentDatasetReadiness | null {
  const { token, indexingStatus, extractionState } = document
  const [readiness, setReadiness] = useState<KnowledgeDocumentDatasetReadiness | null>(null)

  useEffect(() => {
    let isCancelled = false
    fetchDatasetReadiness(token)
      .then((next) => {
        if (!isCancelled) {
          setReadiness(next)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setReadiness(null)
        }
      })
    return () => {
      isCancelled = true
    }
  }, [token, indexingStatus, extractionState])

  return readiness
}
